import { ChangeEvent, useMemo } from "react";
import { useGetCertificatesQuery } from "../../../common/api/certificate";
import { SortEnum } from "../../../common/enums/SortEnum";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useEmployeeId } from "../../../common/utils/useEmployeeId";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
    setExpiringSoonCertificatesCurrentPage,
    setExpiringSoonCertificatesItemsPerPage,
} from "../DashboardSlice";
import { mapExpiringSoonCertificatesToRows } from "../utils/mapExpiringSoonCertificatesToRows";
import { TableWidgetRow } from "@hatch-digital-nl/abs-storybook/legacy";

type UseDashboardExpiringSoonCertificatesResult = {
    loading: boolean;
    table: {
        columns: string[];
        rows: TableWidgetRow[];
        total: number;
    };
    currentPage: number;
    itemsPerPage: number;
    itemsPerPageSelect: { value: number; activeValue: boolean }[];
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    title: string;
    emptyResultText: string;
};

export const useDashboardExpiringSoonCertificates =
    (): UseDashboardExpiringSoonCertificatesResult => {
        const dispatch = useAppDispatch();
        const {
            expiringSoonCertificates: {
                currentPage,
                itemsPerPage,
                itemsPerPageSelect,
            },
        } = useAppSelector((state) => state.dashboard);

        const establishmentId = useEstablishmentId();
        const employeeId = useEmployeeId();

        const { data, isLoading, isFetching } = useGetCertificatesQuery({
            filter: {
                expiring_soon: true,
                establishment_id: establishmentId,
                employee_id: employeeId,
            },
            page: currentPage,
            items_per_page: itemsPerPage,
            sort: SortEnum.EXPIRY_DATE_ASC,
        });

        const loading = isLoading || isFetching;

        const table = useMemo(() => {
            return {
                columns: ["Naam certificaat", "Verloopt op"] as string[],
                rows: mapExpiringSoonCertificatesToRows({
                    certificates: data?.data ?? [],
                }),
                total: data?.meta?.total ?? 0,
            };
        }, [data]);

        const onPageChange = (page: number): void => {
            dispatch(setExpiringSoonCertificatesCurrentPage(page));
        };

        const onItemsPerPageChange = (
            event: ChangeEvent<HTMLSelectElement>,
        ): void => {
            dispatch(
                setExpiringSoonCertificatesItemsPerPage(
                    Number(event.target.value),
                ),
            );
        };

        const title = `Verloopt binnenkort (${data?.meta?.total ?? 0})`;

        return {
            loading,
            table,
            currentPage,
            itemsPerPage,
            itemsPerPageSelect,
            onPageChange,
            onItemsPerPageChange,
            title,
            emptyResultText:
                "Er zijn geen certificaten die binnenkort verlopen",
        };
    };
